<template>
  <div v-if="!destroyed"
       :id="$attrs['id']"
       :class="[{'hide': !visible}]"
       class="modal__backdrop"
       @click="onClickOutside()">
    <div class="modal__content" @click="$event.stopPropagation();">
      <hm-col :grid="true" cols="12">
        <card :title="title || $t('modal.title')" :min-height="minHeight" cols="12" guide="none">
          <template slot="header-right">
            <div class="modal__header__btn modal__header__btn--close">
              <input-icon-button icon="close" @click="onClickCancel" />
            </div>
          </template>
          <hm-card-inner v-if="!isEmpty(warning)" :full-size="true">
            <hm-col cols="12">
              <span v-if="!isEmpty(warning)" class="warning-text warning-text--full-size warning-text--no-margin col-12" v-html="warning" />
            </hm-col>
          </hm-card-inner>
          <hm-card-inner v-if="hasContent()">
            <hm-col cols="12">
              <p v-html="content"></p>
              <p v-if="!isEmpty(hint)" class="text--italic" v-html="hint"></p>
            </hm-col>
            <slot name="toggle" />
          </hm-card-inner>
          <hm-card-inner v-else-if="!isEmpty(warning) || !isEmpty(hint)">
            <hm-col cols="12">
              <span v-if="!isEmpty(warning)" class="warning-text col-12" v-html="warning" />
              <p v-if="!isEmpty(hint)" class="text--italic" v-html="hint"></p>
            </hm-col>
          </hm-card-inner>
          <slot />
          <hm-card-inner v-if="!hideButtons">
            <slot name="buttons">
              <template v-if="!isNil(button1) || !isNil(button2)">
                <hm-button v-if="!isNil(button1)"
                           :cols="`12@mobile ${isNil(button3) ? '6' : '4'}`"
                           :class="[ `btn--${button1.color || 'red'} btn--no-margin-bottom`, { 'btn--no-margin@tablet': isNil(button3) } ]"
                           :icon-left="button1.icon || 'save'"
                           :disabled="button1Disabled()"
                           @click="throttledOnClickButton1">{{ button1.label }}</hm-button>
                <hm-button v-if="!isNil(button2)"
                           :cols="`12@mobile ${isNil(button3) ? '6' : '4'}`"
                           :class="[ `btn--${button2.color || 'green'} btn--no-margin-bottom`, { 'btn--no-margin@tablet': isNil(button3) } ]"
                           :icon-left="button2.icon || 'save'"
                           :disabled="button2Disabled()"
                           @click="throttledOnClickButton2">{{ button2.label }}</hm-button>
                <hm-button v-if="!isNil(button3)"
                           :cols="`12@mobile ${isNil(button3) ? '6' : '4'}`"
                           :class="[ `btn--${button3.color || 'green'} btn--no-margin-bottom`, { 'btn--no-margin@tablet': isNil(button3) } ]"
                           :icon-left="button3.icon || 'save'"
                           :disabled="button3Disabled()"
                           @click="throttledOnClickButton3">{{ button3.label }}</hm-button>
              </template>
              <template v-else>
                <hm-button v-if="invertButtons"
                           :cols="`12@mobile 6`"
                           :disabled="button2Disabled()"
                           class="btn--green btn--no-margin@tablet btn--no-margin-bottom btn--no-margin-top@mobile"
                           icon-left="save"
                           @click="throttledOnClickConfirm">{{ confirmLabel || $t('modal.confirm') }}</hm-button>
                <hm-button v-if="invertButtons && !isEmpty(additionalLabel)"
                           :cols="`12@mobile 6`"
                           class="btn--green btn--no-margin@tablet btn--no-margin-bottom"
                           icon-left="save"
                           @click="throttledOnClickAdditional">{{ additionalLabel }}</hm-button>
                <hm-button v-if="isEmpty(additionalLabel)"
                           :cols="`12@mobile 6`"
                           :class="{ 'btn--no-margin@tablet': isEmpty(additionalLabel) }"
                           :disabled="button1Disabled()"
                           class="btn--red btn--no-margin-bottom"
                           icon-left="close"
                           @click="throttledOnClickCancel">{{ cancelLabel || $t('modal.cancel') }}</hm-button>
                <hm-button v-if="!invertButtons && !isEmpty(additionalLabel)"
                           :cols="`12`"
                           class="btn--green btn--no-margin@tablet btn--no-margin-bottom"
                           icon-left="save"
                           @click="throttledOnClickAdditional">{{ additionalLabel }}</hm-button>
                <hm-button v-if="!invertButtons"
                           :cols="`12@mobile 6`"
                           :disabled="button2Disabled()"
                           class="btn--green btn--no-margin@tablet btn--no-margin-bottom"
                           icon-left="save"
                           @click="throttledOnClickConfirm">{{ confirmLabel || $t('modal.confirm') }}</hm-button>
              </template>
            </slot>
          </hm-card-inner>
        </card>
      </hm-col>
    </div>
  </div>
</template>

<script>

import { isEmpty, isNil, throttle, isFunction } from 'lodash';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'Modal',

  mixins: [HotelmeisterMixin, TimerMixin],

  props: {
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: false,
    },
    content: {
      type: String,
      required: false,
    },
    invertButtons: {
      type: Boolean,
      default: false,
      required: false,
    },
    additionalLabel: {
      type: String,
      required: false,
      default: null,
    },
    confirmLabel: {
      type: String,
      required: false,
      default: null,
    },
    cancelLabel: {
      type: String,
      required: false,
      default: null,
    },
    hideOnSubmit: {
      type: Boolean,
      required: false,
      default: true,
    },
    minHeight: {
      type: String,
      required: false,
      default: '100px',
    },
    hideButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
    throttle: {
      type: Number,
      default: 1000,
    },
    button1: {
      type: Object,
      default: null,
    },
    button2: {
      type: Object,
      default: null,
    },
    button3: {
      type: Object,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    warning: {
      type: String,
      default: null,
    },
    button1Disabled: {
      type: Function,
      default: () => false,
    },
    button2Disabled: {
      type: Function,
      default: () => false,
    },
    button3Disabled: {
      type: Function,
      default: () => false,
    },
  },

  data() {
    return {
      visible: true,
      destroyed: false,
      throttledOnClickConfirm: throttle(this.onClickConfirm, this.throttle, { leading: true, trailing: false }),
      throttledOnClickAdditional: throttle(this.onClickAdditional, this.throttle, { leading: true, trailing: false }),
      throttledOnClickCancel: throttle(this.onClickCancel, this.throttle, { leading: true, trailing: false }),
      throttledOnClickButton1: throttle(() => { this.onClickButton('button1'); }, this.throttle, { leading: true, trailing: false }),
      throttledOnClickButton2: throttle(() => { this.onClickButton('button2'); }, this.throttle, { leading: true, trailing: false }),
      throttledOnClickButton3: throttle(() => { this.onClickButton('button3'); }, this.throttle, { leading: true, trailing: false }),
    };
  },

  mounted: function () {
    this.$emit('mounted');
  },

  created: function () {
    this.$serviceBus.$on('shortcut.esc', this.onClickCancel);
  },

  beforeDestroy() {
    this.$serviceBus.$off('shortcut.esc', this.onClickCancel);
  },

  methods: {
    onClickButton: function (button) {
      if (this.hideOnSubmit === true) this.hide();
      this.$emit('button');

      this.addUIBreadcrumb(
        `User clicked ${button} in modal`,
        {
          title: this.title,
          content: this.content,
        },
      );

      if (!isNil(this[button]) && !isNil(this[button].onClick) && isFunction(this[button].onClick)) {
        this[button].onClick();
      }
    },
    onClickAdditional: function () {
      if (this.hideOnSubmit === true) this.hide();
      this.$emit('additional');

      this.addUIBreadcrumb(
        'User clicked additional in modal',
        {
          title: this.title,
          content: this.content,
        },
      );
    },
    onClickConfirm: function () {
      if (this.hideOnSubmit === true) this.hide();
      this.$emit('confirm');

      this.addUIBreadcrumb(
        'User clicked confirm in modal',
        {
          title: this.title,
          content: this.content,
        },
      );
    },
    onClickCancel: function () {
      this.hide();
      this.$emit('cancel');

      this.addUIBreadcrumb(
        'User clicked cancel in modal',
        {
          title: this.title,
          content: this.content,
        },
      );
    },
    onClickOutside: function () {
      if (this.closeOnClickOutside) {
        this.onClickCancel();
      }
    },
    show: function () {
      this.visible = true;

      this.addUIBreadcrumb(
        'Show modal',
        {
          title: this.title,
          content: this.content,
        },
      );

      this.$emit('show');
    },
    hide: function () {
      this.visible = false;
      this.setTimeout(() => {
        this.destroyed = true;
        this.$emit('destroy');
      }, 150);

      this.addUIBreadcrumb(
        'Hide modal',
        {
          title: this.title,
          content: this.content,
        },
      );

      this.$emit('hide');
    },
    hasContent: function () {
      return !isEmpty(this.content);
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '~styles/import';

  .modal {
    &__backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0,0,0, 0.35);
      z-index: 99999;
      transition: opacity 0.15s ease;
      opacity: 1;
      // backdrop-filter: blur(2px) grayscale(60%);
      backdrop-filter: saturate(90%) blur(2px);
      justify-content: center;
      align-content: center;
      align-items: center;
      justify-items: center;
      display: flex;
    }

    &__content {
      /*
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
       */
      width: 600px;
      max-width: 95vw;
      transition: opacity 0.15s ease;
      opacity: 1;
      max-height: calc(100vh - 6rem - 5rem);
      margin-top: 6rem;
      display: flex;
      justify-content: center;
      align-items: center;

      /deep/ .card {
        box-shadow: 0 6px 30px 0 rgba($black,0.35);
      }

      /deep/ .card__content {
        max-height: calc(90vh - 60px);
        overflow-y: auto;
      }
    }

    &__header__btn {
      height: 4rem;
      width: 4rem;
      position: relative;
      display: inline-block;
      float: right;
      cursor: pointer;

      @include icon-color($middlegray);
      @include icon-hover($text);
    }
  }

  .hide {
    opacity: 0!important;
  }

</style>



// WEBPACK FOOTER //
// src/components/lib/util/Modal.vue